/**
 * Se filmer pendant un exercice (défi streamé, 31/10/2026). L'élève l'accepte au début (icône caméra), peut
 * couper à tout moment ; sinon l'enregistrement s'arrête 5 s après la note. La chronologie de l'exercice suit :
 * chaque question affichée, chaque réponse, la note.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { STREAM_STOP_AFTER_GRADE_MS } from '@jeraisonne/db';
import {
  ExerciseRecorder,
  canRecordExercise,
  type RecordedStream,
  type StreamMark,
} from '@/lib/challenges/exercise-recorder';

/**
 * `done` = vidéo prête, en attente d'un choix ; `discarded` = annulée par l'élève ; `published` = publiée en défi.
 * Tant qu'elle est `starting` / `recording` / `done`, l'écran de note garde « Continuer » caché.
 */
export type StreamStatus =
  | 'off'
  | 'starting'
  | 'recording'
  | 'done'
  | 'discarded'
  | 'published'
  | 'error';

/** La vidéo attend encore un choix de l'élève (arrêter, annuler ou publier). PUR. */
export function streamPending(status: StreamStatus): boolean {
  return status === 'starting' || status === 'recording' || status === 'done';
}

export interface ExerciseStream {
  supported: boolean;
  status: StreamStatus;
  /** flux de la caméra (aperçu pendant l'enregistrement) */
  preview: MediaStream | null;
  /** la vidéo enregistrée, une fois arrêtée */
  result: (RecordedStream & { url: string }) | null;
  error: string | null;
  start: () => void;
  stop: () => void;
  /** jette la vidéo (en cours ou finie) : rien n'est gardé */
  discard: () => void;
  /** la vidéo est partie en publication */
  markPublished: () => void;
  mark: (e: StreamMark) => void;
  /** temps écoulé (ms), pour le compteur */
  elapsed: () => number;
}

export function useExerciseStream({
  questionId,
  grade,
}: {
  /** la question affichée (marquée à chaque changement) */
  questionId: string | null;
  /** la note, dès qu'elle s'affiche : l'enregistrement s'arrête 5 s après */
  grade: { correct: number; total: number } | null;
}): ExerciseStream {
  const recorderRef = useRef<ExerciseRecorder | null>(null);
  const [status, setStatus] = useState<StreamStatus>('off');
  const [preview, setPreview] = useState<MediaStream | null>(null);
  const [result, setResult] = useState<ExerciseStream['result']>(null);
  const [error, setError] = useState<string | null>(null);
  // une vidéo annulée pendant l'enregistrement ne doit pas réapparaître à l'arrêt
  const discardedRef = useRef(false);

  const stop = useCallback(() => {
    const rec = recorderRef.current;
    if (!rec || !rec.recording) return;
    void rec.stop().then((r) => {
      setPreview(null);
      if (discardedRef.current) return;
      setResult({ ...r, url: URL.createObjectURL(r.blob) });
      setStatus('done');
    });
  }, []);

  const discard = useCallback(() => {
    discardedRef.current = true;
    const rec = recorderRef.current;
    if (rec?.recording) void rec.stop().then(() => setPreview(null));
    setResult(null);
    setStatus('discarded');
  }, []);

  const markPublished = useCallback(() => setStatus('published'), []);

  const start = useCallback(() => {
    if (recorderRef.current || !canRecordExercise()) return;
    setStatus('starting');
    setError(null);
    ExerciseRecorder.start(() => stop())
      .then((rec) => {
        recorderRef.current = rec;
        // annulée pendant que la caméra démarrait : on l'éteint aussitôt
        if (discardedRef.current) {
          void rec.stop();
          return;
        }
        setPreview(rec.stream);
        setStatus('recording');
      })
      .catch(() => {
        setStatus('error');
        setError('Caméra ou micro refusés : tu peux continuer l’exercice sans te filmer.');
      });
  }, [stop]);

  const mark = useCallback((e: StreamMark) => recorderRef.current?.mark(e), []);
  const elapsed = useCallback(() => recorderRef.current?.elapsed() ?? 0, []);

  // la question affichée, dès le début et à chaque changement
  useEffect(() => {
    if (status === 'recording' && questionId) mark({ kind: 'question', block_id: questionId });
  }, [status, questionId, mark]);

  // la note : on la pose, puis l'enregistrement s'arrête 5 s plus tard
  const gradeKey = grade ? `${grade.correct}/${grade.total}` : null;
  useEffect(() => {
    if (status !== 'recording' || !grade) return;
    mark({ kind: 'grade', correct: grade.correct, total: grade.total });
    const id = window.setTimeout(stop, STREAM_STOP_AFTER_GRADE_MS);
    return () => window.clearTimeout(id);
    // la note change une seule fois
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, gradeKey, mark, stop]);

  // en quittant l'exercice : la caméra s'éteint, l'adresse de la vidéo est libérée
  useEffect(
    () => () => {
      const rec = recorderRef.current;
      if (rec?.recording) void rec.stop();
    },
    [],
  );
  useEffect(
    () => () => {
      if (result) URL.revokeObjectURL(result.url);
    },
    [result],
  );

  return {
    supported: canRecordExercise(),
    status,
    preview,
    result,
    error,
    start,
    stop,
    discard,
    markPublished,
    mark,
    elapsed,
  };
}
